import { useState, useEffect } from "react";
import { CheckCircle, AlertCircle, Loader, ShieldCheck } from "lucide-react";
import { api } from "../api";
import { calculateDateMetadata, calculationLabel, isFutureDate, todayISO } from "../utils/dateCalculations";
import "../styles.css";

function normalizeAnswerValue(field, rawValue) {
  if (field.type === "date" && field.dateCalculation) {
    return {
      value: rawValue,
      calculated: calculateDateMetadata(rawValue, field.dateCalculation),
    };
  }
  return rawValue;
}

function getFieldDisplayValue(field, value) {
  if (field.type === "date" && value && typeof value === "object" && "value" in value) {
    return value.value || "";
  }
  return value || "";
}

function Field({ field, value, onChange, onFile }) {
  const common = { className: "form-input", required: field.required };
  const inputValue = getFieldDisplayValue(field, value);

  if (field.type === "textarea") {
    return <textarea {...common} rows={4} value={inputValue} onChange={(event) => onChange(event.target.value)} />;
  }
  if (field.type === "number") {
    return <input {...common} type="number" value={inputValue} onChange={(event) => onChange(event.target.value)} />;
  }
  if (field.type === "date") {
    const future = field.dateCalculation && isFutureDate(inputValue);
    return (
      <>
        <input
          {...common}
          type="date"
          max={field.dateCalculation ? todayISO() : undefined}
          value={inputValue}
          onChange={(event) => onChange(event.target.value)}
          style={future ? { borderColor: "#DC2626" } : undefined}
        />
        {future && (
          <div style={{ marginTop: 6, fontSize: "0.78rem", color: "#DC2626" }}>
            La fecha no puede ser posterior a hoy.
          </div>
        )}
        {field.dateCalculation && (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
              {calculationLabel(field.dateCalculation)}
            </label>
            <input
              className="form-input"
              readOnly
              tabIndex={-1}
              value={future ? "" : (value?.calculated?.value || "")}
              placeholder="Se calcula al elegir la fecha"
              style={{ background: "var(--bg, #F8FAFC)", color: "var(--primary)", fontWeight: 600, cursor: "default" }}
            />
          </div>
        )}
      </>
    );
  }
  if (field.type === "select") {
    return (
      <select {...common} value={inputValue} onChange={(event) => onChange(event.target.value)}>
        <option value="" disabled>Selecciona una opcion</option>
        {(field.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    );
  }
  if (field.type === "boolean") {
    return (
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.85rem" }}>
        <input type="checkbox" checked={!!value} onChange={(event) => onChange(event.target.checked)} />
        Si
      </label>
    );
  }
  if (field.type === "file") {
    return (
      <input
        type="file"
        accept=".pdf,.xlsx,.xls,.docx,.doc,.png,.jpg,.jpeg"
        required={field.required}
        onChange={(event) => onFile(event.target.files[0] || null)}
      />
    );
  }
  return <input {...common} type="text" value={inputValue} onChange={(event) => onChange(event.target.value)} />;
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 560, background: "#fff", borderRadius: 14, border: "1px solid var(--border)", boxShadow: "0 4px 24px rgba(0,0,0,0.06)", padding: 32 }}>
        {children}
      </div>
    </div>
  );
}

export default function PublicFormPage({ token }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    api.getPublicForm(token).then((data) => {
      if (data?.error) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setForm(data);
      setLoading(false);
    }).catch(() => {
      setNotFound(true);
      setLoading(false);
    });
  }, [token]);

  const setAnswer = (field, rawValue) => {
    setAnswers((prev) => ({ ...prev, [field.id]: normalizeAnswerValue(field, rawValue) }));
  };
  const setFile = (fieldId, file) => setFiles((prev) => ({ ...prev, [fieldId]: file }));

  const requiredMissing = form?.fields?.some((field) => {
    if (!field.required) return false;
    if (field.type === "file") return !files[field.id];
    const value = answers[field.id];
    if (field.type === "date" && field.dateCalculation) return !value?.value;
    return value === undefined || value === null || value === "";
  });

  const hasFutureDate = form?.fields?.some((field) =>
    field.type === "date" && field.dateCalculation && isFutureDate(answers[field.id]?.value)
  );

  const canSubmit = consent && !requiredMissing && !hasFutureDate && status !== "sending";

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setStatus("sending");
    setErrorMsg("");

    try {
      const formData = new FormData();
      formData.append("consent", "true");
      formData.append("answers", JSON.stringify(answers));
      form.fields.forEach((field) => {
        if (field.type === "file" && files[field.id]) formData.append(`field_${field.id}`, files[field.id]);
      });
      const result = await api.submitPublicForm(token, formData);
      if (result.error) throw new Error(result.error);
      setStatus("ok");
    } catch (error) {
      setErrorMsg(error.message);
      setStatus("error");
    }
  };

  if (loading) {
    return <Shell><div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Loader size={32} className="spin" opacity={0.4} /></div></Shell>;
  }

  if (notFound) {
    return (
      <Shell>
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <AlertCircle size={44} style={{ color: "var(--text-muted)" }} />
          <h2 style={{ marginTop: 16 }}>Formulario no disponible</h2>
          <p style={{ color: "var(--text-muted)", marginTop: 8, fontSize: "0.88rem" }}>
            Este enlace no es valido o el formulario ya fue cerrado. Comunicate con quien te lo compartio.
          </p>
        </div>
      </Shell>
    );
  }

  if (status === "ok") {
    return (
      <Shell>
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <CheckCircle size={48} className="success-icon" />
          <h2 style={{ marginTop: 16 }}>Respuesta enviada</h2>
          <p style={{ color: "var(--text-muted)", marginTop: 8, fontSize: "0.88rem" }}>
            Gracias, tu informacion fue registrada correctamente.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 style={{ fontSize: "1.2rem", fontWeight: 700, letterSpacing: "-0.4px" }}>{form.name}</h1>
      {form.description && <p style={{ color: "var(--text-muted)", fontSize: "0.88rem", marginTop: 6 }}>{form.description}</p>}

      <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 24 }}>
        {form.fields.map((field) => (
          <div className="form-row" key={field.id}>
            <label>{field.label}{field.required ? " *" : ""}</label>
            {field.description && (
              <div style={{ marginTop: -8, marginBottom: 8, fontSize: "0.8rem", color: "var(--text-muted)", lineHeight: 1.45 }}>
                {field.description}
              </div>
            )}
            <Field
              field={field}
              value={answers[field.id]}
              onChange={(value) => setAnswer(field, value)}
              onFile={(file) => setFile(field.id, file)}
            />
          </div>
        ))}
      </div>

      <div style={{ marginTop: 24, padding: "14px 16px", background: "var(--bg)", borderRadius: 10, border: "1px solid var(--border)" }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
          <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} style={{ marginTop: 2 }} />
          <span style={{ fontSize: "0.78rem", color: "var(--text-muted)", lineHeight: 1.5 }}>
            <ShieldCheck size={13} style={{ display: "inline", marginRight: 4, verticalAlign: "-2px" }} />
            Autorizo el tratamiento de mis datos personales conforme a la Ley 1581 de 2012 y sus decretos reglamentarios,
            para los fines relacionados con el Sistema de Gestion de Seguridad y Salud en el Trabajo (SG-SST) de la organizacion.
          </span>
        </label>
      </div>

      {status === "error" && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#FEE2E2", borderRadius: 8, color: "#C53030", fontSize: "0.78rem", marginTop: 14 }}>
          <AlertCircle size={15} /> {errorMsg}
        </div>
      )}

      <button className="btn-primary" onClick={handleSubmit} disabled={!canSubmit} style={{ width: "100%", justifyContent: "center", marginTop: 20, padding: 12 }}>
        {status === "sending" ? <><Loader size={15} className="spin" /> Enviando...</> : "Enviar respuesta"}
      </button>
    </Shell>
  );
}
